import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { getKey } from "@redotech/react-util/key";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { Printer } from "@redotech/redo-fulfillment-common/printer";
import { PageFormat } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import type { OutboundLabelSettings } from "@redotech/redo-fulfillment-settings/outbound-label-settings";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import PrinterIcon from "@redotech/redo-icons/printer.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import {
  getCurrentTeam,
  updateCurrentTeam,
} from "@redotech/redo-merchant-app-common/client/team";
import { GlobalAgentContext } from "@redotech/redo-merchant-app-common/global-agent-context";
import {
  ReloadTeamContext,
  TeamContext,
} from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { Preset } from "@redotech/redo-model/outbound-labels/presets";
import { toast } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoExternalLink } from "@redotech/redo-web/arbiter-components/links/redo-external-link";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTableColumn } from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { RedoTableCard } from "@redotech/redo-web/arbiter-components/tables/redo-table-card";
import { RedoTableTextCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { nonEmptyValidator } from "@redotech/ui/validators";
import { memo, useContext, useEffect, useMemo, useState } from "react";
import { PrinterContext } from "../common/printer-context";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import * as printSetupStyles from "./print-setup.module.css";

enum PrinterType {
  LABEL = "label",
  LETTER = "letter",
  PREMIUM = "premium",
  BARCODE = "barcode",
}

type Workstation = NonNullable<OutboundLabelSettings["workstations"]>[number];

export const RedoConnectSettings = memo(function RedoConnectSettings() {
  const outboundLabelsClient = useRequiredContext(
    RedoOutboundLabelsRpcClientContext,
  );
  const team = useRequiredContext(TeamContext);
  const merchantClient = useRequiredContext(RedoMerchantClientContext);
  const reloadTeam = useRequiredContext(ReloadTeamContext);
  const { setPageContext } = useContext(GlobalAgentContext);

  useEffect(() => {
    setPageContext?.({
      pageTitle: "Print setup",
      pageDescription:
        "The merchant is on the OMS Print setup and printer configuration settings page",
      preferredAgent: "oms",
      preferredSkill: "oms",
    });
    return () => setPageContext?.(undefined);
  }, [setPageContext]);

  const [printNodeOpen, setPrintNodeOpen] = useState(false);
  const [printNodeSigninOpen, setPrintNodeSigninOpen] = useState(false);
  const [printNodePasswordOpen, setPrintNodePasswordOpen] = useState(false);
  const [workstationOpen, setWorkstationOpen] = useState(false);
  const [selectedWorkstation, setSelectedWorkstation] =
    useState<Workstation | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [workstationToDelete, setWorkstationToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const printNodeLoad = useLoad(
    async (signal) =>
      await outboundLabelsClient.getPrintNodeInfo(
        { refresh: false },
        { signal },
      ),
    [outboundLabelsClient],
  );

  const presetsLoad = useLoad(async () => {
    const result = await outboundLabelsClient.getPresets({});
    return result.presets;
  }, [outboundLabelsClient]);

  const handleDeleteWorkstation = useHandler(async () => {
    if (!workstationToDelete) {
      return;
    }

    try {
      const { team } = await getCurrentTeam(merchantClient, {});

      const workstations = team.settings.outboundLabels.workstations || [];
      const updatedWorkstations = workstations.filter(
        (w) => w._id !== workstationToDelete.id,
      );

      team.settings.outboundLabels.workstations = updatedWorkstations;

      await updateCurrentTeam(merchantClient, { team });
      reloadTeam();
      setDeleteConfirmOpen(false);
      setWorkstationToDelete(null);
    } catch (error) {
      console.error("Error deleting workstation:", error);
    }
  });

  const onDeleteWorkstation = useHandler((id: string, name: string) => {
    setWorkstationToDelete({ id, name });
    setDeleteConfirmOpen(true);
  });

  return (
    <Flex dir="column" gap="xl" w="lg">
      <Text fontSize="md" fontWeight="semibold">
        Redo Connect
      </Text>
      {printNodeLoad.value === undefined ? null : printNodeLoad.value ===
        null ? (
        <>
          <Flex flexDirection="row">
            <RedoButton
              onClick={() => setPrintNodeSigninOpen(true)}
              text="Sign in"
            />
            <RedoButton
              onClick={() => setPrintNodeOpen(true)}
              text="Create account"
            />
          </Flex>
          <PrintNodeSetupModal
            onClose={() => setPrintNodeOpen(false)}
            open={printNodeOpen}
          />
          <PrintNodeSigninModal
            onClose={() => setPrintNodeSigninOpen(false)}
            open={printNodeSigninOpen}
          />
        </>
      ) : (
        <>
          <Card headerExtra={<RedoBadge text="Connected" />}>
            <Flex dir="row" justify="space-between" w="full">
              <Text fontSize="sm">Email: {printNodeLoad.value.email}</Text>
              <Flex dir="column" gap="sm">
                <Tooltip
                  title={
                    !printNodeLoad.value.hasPassword
                      ? "Password must be updated to support sharing Redo Connect across stores"
                      : undefined
                  }
                >
                  <Flex>
                    <RedoButton
                      hierarchy="secondary"
                      onClick={() => setPrintNodePasswordOpen(true)}
                      size="sm"
                      text="Change password"
                      theme="normal"
                    />
                  </Flex>
                </Tooltip>
              </Flex>
            </Flex>
            <Flex flexDirection="column">
              <Text fontSize="sm">Desktop clients:</Text>
              <RedoExternalLink
                text="MacOS"
                url="https://app.printnode.com/download/client/getredo/osx"
              />
              <RedoExternalLink
                text="Windows"
                url="https://app.printnode.com/download/client/getredo/windows"
              />
            </Flex>
          </Card>
          <RedoTableCard
            rightButtonProps={{
              IconLeading: PlusIcon,
              hierarchy: "secondary",
              onClick: () => setWorkstationOpen(true),
              size: "sm",
              text: "Add",
              theme: "normal",
            }}
            tableProps={{
              layout: { columns: workstationColumns(onDeleteWorkstation) },
              rowsOfData: team.settings.outboundLabels?.workstations ?? [],
              emptyStateContent: (
                <Flex align="center" flexDirection="column" gap="md" p="lg">
                  <Text>No workstations found.</Text>
                </Flex>
              ),
              onRowClick: (row) => {
                setSelectedWorkstation(row);
                setWorkstationOpen(true);
              },
            }}
            title="Workstations"
          />
          <WorkstationSettingsModal
            key={getKey(selectedWorkstation)}
            onClose={() => {
              setWorkstationOpen(false);
              setSelectedWorkstation(null);
            }}
            open={workstationOpen}
            presets={presetsLoad.value}
            workstation={selectedWorkstation}
          />
          <RedoModal
            isOpen={deleteConfirmOpen}
            onModalCloseRequested={() => {
              setDeleteConfirmOpen(false);
              setWorkstationToDelete(null);
            }}
            primaryButton={{
              text: "Delete",
              onClick: handleDeleteWorkstation,
              theme: "destructive",
            }}
            secondaryButton={{
              text: "Cancel",
              onClick: () => {
                setDeleteConfirmOpen(false);
                setWorkstationToDelete(null);
              },
            }}
            title="Delete Workstation"
          >
            <Text>
              Are you sure you want to delete the workstation "
              {workstationToDelete?.name}"? This action cannot be undone.
            </Text>
          </RedoModal>
          <PrintNodePasswordModal
            email={printNodeLoad.value?.email}
            onClose={() => setPrintNodePasswordOpen(false)}
            open={printNodePasswordOpen}
          />
        </>
      )}
    </Flex>
  );
});

const printNodeSetupForm = groupInput({
  email: input<string>({ validator: nonEmptyValidator }),
  password: input<string>({ validator: nonEmptyValidator }),
});

const printNodePasswordForm = groupInput({
  newPassword: input<string>({ validator: nonEmptyValidator }),
});

export const PrintNodeSetupModal = memo(function PrintNodeSetupModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const user = useContext(UserContext);
  const input = useInput(printNodeSetupForm, {
    email: user?.email ?? "",
    password: "",
  });

  const handleCreate = useHandler(async () => {
    const { email, password } = input.value;
    const { success, message } = await client.createPrintNodeAccount({
      email,
      password,
    });
    if (success) {
      location.reload();
    } else {
      toast(message ?? "Failed to create account", { variant: "error" });
    }
  });

  return (
    <RedoModal
      isOpen={open}
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Signup",
        onClick: handleCreate,
        disabled: input.allErrors.length > 0,
      }}
      secondaryButton={{ text: "Close", onClick: onClose }}
      title="Redo Connect Setup"
    >
      <Flex flexDirection="column" gap="sm">
        <RedoTextInput
          inputTypeHint="email"
          label="Email"
          setValue={input.inputs.email.setValue}
          value={input.inputs.email.value}
        />
        <RedoTextInput
          description="You'll use this password to sign into Redo Connect on your desktop"
          inputTypeHint="password"
          label="Password"
          setValue={input.inputs.password.setValue}
          value={input.inputs.password.value}
        />
      </Flex>
    </RedoModal>
  );
});

export const PrintNodeSigninModal = memo(function PrintNodeSigninModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const input = useInput(printNodeSetupForm, { email: "", password: "" });

  const handleSignin = useHandler(async () => {
    const { email, password } = input.value;
    const { success, message } = await client.signInPrintNodeAccount({
      email,
      password,
    });
    if (success) {
      location.reload();
    } else {
      toast(message, { variant: "error" });
    }
  });

  return (
    <RedoModal
      isOpen={open}
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Signin",
        onClick: handleSignin,
        disabled: input.allErrors.length > 0,
      }}
      secondaryButton={{ text: "Close", onClick: onClose }}
      title="Redo Connect Setup"
    >
      <Flex flexDirection="column" gap="sm">
        <RedoTextInput
          inputTypeHint="email"
          label="Email"
          setValue={input.inputs.email.setValue}
          value={input.inputs.email.value}
        />
        <RedoTextInput
          inputTypeHint="password"
          label="Password"
          setValue={input.inputs.password.setValue}
          value={input.inputs.password.value}
        />
      </Flex>
    </RedoModal>
  );
});

export const PrintNodePasswordModal = memo(function PrintNodePasswordModal({
  open,
  onClose,
  email,
}: {
  open: boolean;
  onClose: () => void;
  email: string;
}) {
  const input = useInput(printNodePasswordForm, { newPassword: "" });

  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const reloadTeam = useRequiredContext(ReloadTeamContext);
  const handleSave = useHandler(async () => {
    const { newPassword } = input.value;
    const { success, message } = await client.updatePrintNodePassword({
      email,
      newPassword,
    });
    if (success) {
      toast("Password updated", { variant: "success" });
      reloadTeam();
      onClose();
    } else {
      toast(message, { variant: "error" });
    }
  });

  return (
    <RedoModal
      isOpen={open}
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Save",
        onClick: handleSave,
        disabled: input.allErrors.length > 0,
      }}
      secondaryButton={{ text: "Close", onClick: onClose }}
      title="Reset redo connect password"
    >
      <Flex flexDirection="column" gap="sm">
        <RedoTextInput
          inputTypeHint="password"
          label="New Password"
          setValue={input.inputs.newPassword.setValue}
          value={input.inputs.newPassword.value}
        />
      </Flex>
    </RedoModal>
  );
});

const workstationColumns = (
  onDelete: (id: string, name: string) => void,
): RedoTableColumn<Workstation>[] => [
  {
    key: "name",
    renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="Name" />,
    renderNormalCell: ({ row }) => <RedoTableTextCell text={row.name} />,
  },
  {
    key: "label-paper",
    renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="4x6" />,
    renderNormalCell: ({ row }) => (
      <RedoTableTextCell
        text={
          row.selectedPrinters.find(
            (printer) => printer.format === PageFormat.Label,
          )?.printerName ?? "Download PDF"
        }
      />
    ),
  },
  {
    key: "packing-slip",
    renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="8.5x11" />,
    renderNormalCell: ({ row }) => (
      <RedoTableTextCell
        text={
          row.selectedPrinters.find(
            (printer) => printer.format === PageFormat.Letter,
          )?.printerName ?? "Download PDF"
        }
      />
    ),
  },
  {
    key: "actions",
    widthMode: { type: "fixed", width: "50px" },
    renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="" />,
    renderNormalCell: ({ row }) => (
      <Flex align="center" justify="flex-end">
        <RedoButton
          IconLeading={TrashIcon}
          onClick={(e) => {
            e.stopPropagation();
            if (row._id) {
              onDelete(row._id, row.name);
            }
          }}
          size="xs"
          theme="destructive"
        />
      </Flex>
    ),
  },
];

const printerSettingsForm = groupInput({
  printerId: input<number>(),
  printerName: input<string>(),
  paper: input<string>(),
  bin: input<string>(),
  rotate: input<0 | 90 | 180 | 270>(),
  fit_to_page: input<boolean>(),
  dpi: input<string>(),
  duplex: input<"long-edge" | "short-edge" | "one-sided" | "">(),
});
type PrinterSettingsForm = InputSchema.Input<typeof printerSettingsForm>;

const workstationSettingsForm = groupInput({
  name: input<string>({ validator: nonEmptyValidator }),
  labelPrinter: printerSettingsForm,
  letterPrinter: printerSettingsForm,
  premiumPrinter: printerSettingsForm,
  barcodePrinter: printerSettingsForm,
  defaultPresetId: input<string | null>(),
});

export const WorkstationSettingsModal = memo(function WorkstationSettingsModal({
  open,
  onClose,
  workstation,
  presets,
}: {
  open: boolean;
  onClose: () => void;
  workstation: Workstation | null;
  presets?: Preset[];
}) {
  const { printers } = useContext(PrinterContext);
  const team = useRequiredContext(TeamContext);

  const [configuringPrinter, setConfiguringPrinter] =
    useState<PrinterType | null>(null);

  const selectedLabelPrinter = workstation?.selectedPrinters?.find(
    (printer) => printer.format === PageFormat.Label && !printer.isPremium,
  );
  const selectedLetterPrinter = workstation?.selectedPrinters?.find(
    (printer) => printer.format === PageFormat.Letter && !printer.isPremium,
  );
  const selectedPremiumPrinter = workstation?.selectedPrinters?.find(
    (printer) => printer.isPremium,
  );
  const input = useInput(workstationSettingsForm, {
    name: workstation?.name ?? "",
    labelPrinter: {
      printerId: selectedLabelPrinter?.printerId ?? 0,
      printerName: selectedLabelPrinter?.printerName ?? "",
      paper: selectedLabelPrinter?.printerOptions?.paper ?? "",
      bin: selectedLabelPrinter?.printerOptions?.bin ?? "",
      rotate: selectedLabelPrinter?.printerOptions?.rotate ?? 0,
      fit_to_page: selectedLabelPrinter?.printerOptions?.fit_to_page ?? false,
      dpi: selectedLabelPrinter?.printerOptions?.dpi ?? "",
      duplex: selectedLabelPrinter?.printerOptions?.duplex ?? ("" as const),
    },
    barcodePrinter: {
      printerId: workstation?.barcodePrinterId ?? 0,
      printerName: workstation?.barcodePrinterName ?? "",
      paper: workstation?.barcodePrinterOptions?.paper ?? "",
      bin: workstation?.barcodePrinterOptions?.bin ?? "",
      rotate: workstation?.barcodePrinterOptions?.rotate ?? 0,
      fit_to_page: workstation?.barcodePrinterOptions?.fit_to_page ?? false,
      dpi: workstation?.barcodePrinterOptions?.dpi ?? "",
      duplex: (workstation?.barcodePrinterOptions?.duplex ??
        "") as DuplexOption,
    },
    letterPrinter: {
      printerId: selectedLetterPrinter?.printerId ?? 0,
      printerName: selectedLetterPrinter?.printerName ?? "",
      paper: selectedLetterPrinter?.printerOptions?.paper ?? "",
      bin: selectedLetterPrinter?.printerOptions?.bin ?? "",
      rotate: selectedLetterPrinter?.printerOptions?.rotate ?? 0,
      fit_to_page: selectedLetterPrinter?.printerOptions?.fit_to_page ?? false,
      dpi: selectedLetterPrinter?.printerOptions?.dpi ?? "",
      duplex: selectedLetterPrinter?.printerOptions?.duplex ?? ("" as const),
    },
    premiumPrinter: {
      printerId: selectedPremiumPrinter?.printerId ?? 0,
      printerName: selectedPremiumPrinter?.printerName ?? "",
      paper: selectedPremiumPrinter?.printerOptions?.paper ?? "",
      bin: selectedPremiumPrinter?.printerOptions?.bin ?? "",
      rotate: selectedPremiumPrinter?.printerOptions?.rotate ?? 0,
      fit_to_page: selectedPremiumPrinter?.printerOptions?.fit_to_page ?? true,
      dpi: selectedPremiumPrinter?.printerOptions?.dpi ?? "",
      duplex: selectedPremiumPrinter?.printerOptions?.duplex ?? ("" as const),
    },
    defaultPresetId: workstation?.defaultPresetId ?? null,
  });

  const presetOptions: RedoListItem<string | null>[] = useMemo(() => {
    const options: RedoListItem<string | null>[] = [
      { id: "none", value: null, type: "text", text: "No default preset" },
    ];
    if (presets) {
      for (const preset of presets) {
        options.push({
          id: preset._id,
          value: preset._id,
          type: "text",
          text: preset.name,
        });
      }
    }
    return options;
  }, [presets]);

  const selectedPresetOption = useMemo(() => {
    return (
      presetOptions.find(
        (opt) => opt.value === input.inputs.defaultPresetId.value,
      ) ?? presetOptions[0]
    );
  }, [presetOptions, input.inputs.defaultPresetId.value]);

  const reloadTeam = useRequiredContext(ReloadTeamContext);
  const merchantClient = useRequiredContext(RedoMerchantClientContext);

  const saveHandler = useHandler(async () => {
    const { team } = await getCurrentTeam(merchantClient, {});

    const value = input.value;

    const selectedPrinters = [];
    if (value.labelPrinter.printerId) {
      selectedPrinters.push({
        format: PageFormat.Label,
        printerId: value.labelPrinter.printerId,
        printerName: value.labelPrinter.printerName,
        printerOptions: {
          paper: value.labelPrinter.paper,
          bin: value.labelPrinter.bin || undefined,
          rotate: value.labelPrinter.rotate,
          fit_to_page: value.labelPrinter.fit_to_page,
          dpi: value.labelPrinter.dpi || undefined,
          duplex: value.labelPrinter.duplex || undefined,
        },
      });
    }
    if (value.letterPrinter.printerId) {
      selectedPrinters.push({
        format: PageFormat.Letter,
        printerId: value.letterPrinter.printerId,
        printerName: value.letterPrinter.printerName,
        printerOptions: {
          paper: value.letterPrinter.paper || undefined,
          bin: value.letterPrinter.bin || undefined,
          rotate: value.letterPrinter.rotate,
          fit_to_page: value.letterPrinter.fit_to_page,
          dpi: value.letterPrinter.dpi || undefined,
          duplex: value.letterPrinter.duplex || undefined,
        },
      });
    }
    if (value.premiumPrinter.printerId) {
      selectedPrinters.push({
        format: PageFormat.Letter,
        printerId: value.premiumPrinter.printerId,
        printerName: value.premiumPrinter.printerName,
        isPremium: true,
        printerOptions: {
          paper: value.premiumPrinter.paper || undefined,
          bin: value.premiumPrinter.bin || undefined,
          rotate: value.premiumPrinter.rotate,
          fit_to_page: value.premiumPrinter.fit_to_page,
          dpi: value.premiumPrinter.dpi || undefined,
          duplex: value.premiumPrinter.duplex || undefined,
        },
      });
    }

    const workstationValue: Omit<Workstation, "_id"> = {
      name: value.name,
      selectedPrinters,
    };
    if (value.barcodePrinter.printerId) {
      workstationValue.barcodePrinterId = value.barcodePrinter.printerId;
      workstationValue.barcodePrinterName = value.barcodePrinter.printerName;
      workstationValue.barcodePrinterOptions = {
        paper: value.barcodePrinter.paper || undefined,
        bin: value.barcodePrinter.bin || undefined,
        rotate: value.barcodePrinter.rotate,
        fit_to_page: value.barcodePrinter.fit_to_page,
        dpi: value.barcodePrinter.dpi || undefined,
        duplex: value.barcodePrinter.duplex || undefined,
      };
    }
    if (workstation?.overridePackingSlipTemplateId) {
      workstationValue.overridePackingSlipTemplateId =
        workstation.overridePackingSlipTemplateId;
    }
    if (value.defaultPresetId !== null) {
      workstationValue.defaultPresetId = value.defaultPresetId;
    }

    const workstations = team.settings.outboundLabels.workstations || [];
    if (workstation?._id) {
      const index = workstations.findIndex((w) => w._id === workstation._id);

      if (index !== -1) {
        workstations[index] = { _id: workstation._id, ...workstationValue };
      }
    } else {
      workstations.push(workstationValue);
    }

    team.settings.outboundLabels.workstations = workstations;

    await updateCurrentTeam(merchantClient, { team });
    reloadTeam();
    onClose();
  });

  const handleModalClose = () => {
    if (configuringPrinter) {
      setConfiguringPrinter(null);
    } else {
      onClose();
    }
  };

  const saveDisabledReason = !input.inputs.name.value.trim()
    ? "Enter a workstation name before saving."
    : input.allErrors.length > 0
      ? "Fix the workstation settings before saving."
      : false;

  return (
    <RedoModal
      isOpen={open}
      onModalCloseRequested={handleModalClose}
      primaryButton={{
        text: "Save",
        onClick: saveHandler,
        disabled: saveDisabledReason,
      }}
      secondaryButton={
        configuringPrinter
          ? { text: "Back", onClick: () => setConfiguringPrinter(null) }
          : undefined
      }
      subtitle={workstation?.name}
      title="Workstation settings"
    >
      <Flex flexDirection="column" gap="sm">
        <RedoTextInput
          label="Name"
          setValue={input.inputs.name.setValue}
          value={input.inputs.name.value}
        />
        {configuringPrinter ? (
          <PrinterSettingsFormSection
            configuringPrinter={configuringPrinter}
            input={input}
            printers={printers}
          />
        ) : (
          <>
            {presets && presets.length > 0 && (
              <RedoSingleSelectDropdownInput<string | null>
                label="Default preset"
                options={presetOptions}
                optionSelected={({ item }) => {
                  input.inputs.defaultPresetId.setValue(item.value);
                }}
                selectedItem={selectedPresetOption}
                size="xs"
              />
            )}
            <Text fontSize="sm" fontWeight="semibold" mb="md">
              Printer Configuration
            </Text>
            <Flex
              className={printSetupStyles.printerCardsContainer}
              dir="row"
              gap="md"
              justify="space-between"
            >
              <PrinterCard
                input={input.inputs.labelPrinter}
                name="Label Printer"
                onClick={() => setConfiguringPrinter(PrinterType.LABEL)}
                printers={printers}
              />
              <PrinterCard
                input={input.inputs.letterPrinter}
                name="Letter Printer"
                onClick={() => setConfiguringPrinter(PrinterType.LETTER)}
                printers={printers}
              />

              {team.settings.outboundLabels?.personalizedSlipsEnabled && (
                <PrinterCard
                  input={input.inputs.premiumPrinter}
                  name="Premium Printer"
                  onClick={() => setConfiguringPrinter(PrinterType.PREMIUM)}
                  printers={printers}
                />
              )}
              <PrinterCard
                input={input.inputs.barcodePrinter}
                name="Barcode Printer"
                onClick={() => setConfiguringPrinter(PrinterType.BARCODE)}
                printers={printers}
              />
            </Flex>
          </>
        )}
      </Flex>
    </RedoModal>
  );
});

const PrinterCard = memo(function PrinterCard({
  name,
  printers,
  input,
  onClick,
}: {
  name: string;
  printers: Printer[];
  input: PrinterSettingsForm;
  onClick: () => void;
}) {
  const selectedPrinter = printers.find(
    (printer) => printer.id === input.inputs.printerId.value,
  );

  const isConfigured = Boolean(input.inputs.printerId.value);
  let errorText: string | undefined;
  if (selectedPrinter) {
    if (selectedPrinter.computer.state !== "connected") {
      errorText = `${selectedPrinter.computer.name} is offline`;
    } else if (selectedPrinter.state !== "online") {
      errorText = "Printer is offline";
    }
  }

  const cardClassNames = [printSetupStyles.printerCard];
  const iconContainerClassNames = [printSetupStyles.printerIconContainer];
  if (isConfigured) {
    cardClassNames.push(printSetupStyles.printerCardConfigured);
    iconContainerClassNames.push(printSetupStyles.printerIconConfigured);
    if (errorText) {
      cardClassNames.push(printSetupStyles.printerCardError);
      iconContainerClassNames.push(printSetupStyles.printerIconError);
    }
  } else {
    iconContainerClassNames.push(printSetupStyles.printerIconUnconfigured);
  }

  return (
    <div className={cardClassNames.join(" ")} onClick={onClick}>
      <Flex align="center" dir="column" gap="sm" p="lg" textAlign="center">
        <div className={iconContainerClassNames.join(" ")}>
          <PrinterIcon className={printSetupStyles.printerIcon} />
        </div>
        <Text fontSize="sm" fontWeight="semibold">
          {name}
        </Text>
        {isConfigured ? (
          <Text fontSize="xs" textColor={errorText ? "error" : "success"}>
            {selectedPrinter?.description ||
              selectedPrinter?.name ||
              "Configured"}
            {errorText && ` - ${errorText}`}
          </Text>
        ) : (
          <Text fontSize="xs" textColor="tertiary">
            Click to configure
          </Text>
        )}
      </Flex>
    </div>
  );
});

const getPrinterDisplayName = (printerType: PrinterType): string => {
  switch (printerType) {
    case PrinterType.LABEL:
      return "Label Printer";
    case PrinterType.LETTER:
      return "Letter Printer";
    case PrinterType.PREMIUM:
      return "Premium Printer";
    case PrinterType.BARCODE:
      return "Barcode Printer";
    default:
      return "Unknown Printer";
  }
};

const getPrinterInput = (
  printerType: PrinterType,
  input: InputSchema.Input<typeof workstationSettingsForm>,
): PrinterSettingsForm => {
  switch (printerType) {
    case PrinterType.LABEL:
      return input.inputs.labelPrinter;
    case PrinterType.LETTER:
      return input.inputs.letterPrinter;
    case PrinterType.PREMIUM:
      return input.inputs.premiumPrinter;
    case PrinterType.BARCODE:
      return input.inputs.barcodePrinter;
    default:
      return input.inputs.labelPrinter;
  }
};

const getPrinterPageFormat = (printerType: PrinterType): PageFormat => {
  switch (printerType) {
    case PrinterType.LABEL:
    case PrinterType.BARCODE:
      return PageFormat.Label;
    case PrinterType.LETTER:
    case PrinterType.PREMIUM:
      return PageFormat.Letter;
    default:
      return PageFormat.Letter;
  }
};

const PrinterSettingsFormSection = memo(function PrinterSettingsFormSection({
  configuringPrinter,
  input,
  printers,
}: {
  configuringPrinter: PrinterType;
  input: InputSchema.Input<typeof workstationSettingsForm>;
  printers: Printer[];
}) {
  const printerInput = getPrinterInput(configuringPrinter, input);
  const pageFormat = getPrinterPageFormat(configuringPrinter);

  return (
    <PrinterSettingsFormComponent
      input={printerInput}
      name={getPrinterDisplayName(configuringPrinter)}
      pageFormat={pageFormat}
      printers={printers}
    />
  );
});

const PrinterSettingsFormComponent = memo(
  function PrinterSettingsFormComponent({
    name,
    printers,
    input,
    pageFormat,
  }: {
    name: string;
    printers: Printer[];
    input: PrinterSettingsForm;
    pageFormat: PageFormat;
  }) {
    const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);

    const printerOptions: RedoListItem<null | Printer>[] = useMemo(
      () => [
        { id: "pdf-download", value: null, type: "text", text: "Download PDF" },
        ...printers.map<RedoListItem<Printer>>((printer) => ({
          id: printer.id,
          value: printer,
          type: "text",
          text: `${printer.description || printer.name}`,
          supportingText: `Connected to ${printer.computer.name || "Unknown"}`,
        })),
      ],
      [printers],
    );

    const selectedPrinter =
      printerOptions.find(
        (option) => option.value?.id === input.inputs.printerId.value,
      ) || printerOptions[0];

    const paperOptions: RedoListItem<string | null>[] = useMemo(
      () => [
        { id: "default", value: null, type: "text", text: "Default" },
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        ...Object.keys(selectedPrinter.value?.capabilities?.papers ?? {}).map<
          RedoListItem<string>
        >((key) => ({ id: key, type: "text", value: key, text: key })),
      ],
      [selectedPrinter],
    );

    const selectedPaper =
      paperOptions.find(({ value }) => value === input.inputs.paper.value) ||
      paperOptions[0];

    const binOptions: RedoListItem<string | null>[] = useMemo(
      () => [
        { id: "default", value: null, type: "text", text: "Default" },
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        ...(selectedPrinter.value?.capabilities?.bins || []).map((bin) => ({
          id: bin,
          type: "text" as const,
          text: bin,
          value: bin,
        })),
      ],
      [selectedPrinter],
    );

    const selectedBin =
      binOptions.find(({ value }) => value === input.inputs.bin.value) ||
      binOptions[0];

    const dpiOptions: RedoListItem<string | null>[] = useMemo(
      () => [
        { id: "default", type: "text", text: "Default", value: null },
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        ...(selectedPrinter.value?.capabilities?.dpis?.map((dpi) => ({
          id: dpi,
          type: "text" as const,
          text: dpi,
          value: dpi,
        })) || []),
      ],
      [selectedPrinter],
    );

    const selectedDpi =
      dpiOptions.find(({ value }) => value === input.inputs.dpi.value) ||
      dpiOptions[0];

    const [testPrintLoad, doTestPrint] = useTriggerLoad(async () => {
      await client.printTest({
        printerId: input.value.printerId,
        paper: selectedPaper?.value ?? undefined,
        bin: selectedBin?.value ?? undefined,
        rotate: input.value.rotate,
        fit_to_page: input.value.fit_to_page,
        dpi: input.value.dpi || undefined,
        duplex: input.value.duplex || undefined,
        pageFormat,
      });
    });

    return (
      <Flex flexDirection="column" gap="sm" mt="lg">
        <Text fontSize="sm" mb="md">
          {name}
        </Text>
        <RedoSingleSelectDropdownInput<Printer | null>
          label="Printer"
          options={printerOptions}
          optionSelected={({ item }) => {
            input.inputs.printerId.setValue(item.value?.id ?? 0);
            input.inputs.printerName.setValue(
              (item.value?.description || item.value?.name) ?? "",
            );
            input.inputs.paper.setValue("");
            input.inputs.bin.setValue("");
            input.inputs.dpi.setValue("");
          }}
          selectedItem={selectedPrinter}
          size="xs"
        />
        {/* @ts-expect-error TODO: noUncheckedIndexedAccess FIXME */}
        {selectedPrinter.value ? (
          <>
            {paperOptions.length > 1 ? (
              <RedoSingleSelectDropdownInput
                label="Paper size"
                options={paperOptions}
                optionSelected={({ item }) => {
                  input.inputs.paper.setValue(item.value ?? "");
                }}
                selectedItem={selectedPaper}
                size="xs"
              />
            ) : null}
            {binOptions.length > 1 ? (
              <RedoSingleSelectDropdownInput
                label="Printer bin"
                options={binOptions}
                optionSelected={({ item }) => {
                  input.inputs.bin.setValue(item.value ?? "");
                }}
                selectedItem={selectedBin}
                size="xs"
              />
            ) : null}
            <RedoSingleSelectDropdownInput<0 | 90 | 180 | 270>
              label="Rotate"
              options={rotateOptions}
              optionSelected={({ item }) => {
                input.inputs.rotate.setValue(item.value);
              }}
              selectedItem={rotateOptions.find(
                (option) => option.value === input.inputs.rotate.value,
              )}
              size="xs"
            />

            {/* @ts-expect-error TODO: noUncheckedIndexedAccess FIXME */}
            {selectedPrinter.value?.capabilities?.dpis?.length ? (
              <RedoSingleSelectDropdownInput
                label="DPI"
                options={dpiOptions}
                optionSelected={({ item }) => {
                  input.inputs.dpi.setValue(item.value ?? "");
                }}
                selectedItem={selectedDpi}
                size="xs"
              />
            ) : null}

            <RedoToggle
              label="Fit to page"
              setValue={input.inputs.fit_to_page.setValue}
              size="xs"
              value={input.inputs.fit_to_page.value}
            />

            <RedoSingleSelectDropdownInput<
              "" | "long-edge" | "short-edge" | "one-sided"
            >
              label="Duplex"
              options={duplexOptions}
              optionSelected={({ item }) => {
                input.inputs.duplex.setValue(item.value);
              }}
              selectedItem={duplexOptions.find(
                (option) => option.value === input.inputs.duplex.value,
              )}
              size="xs"
            />

            <RedoButton
              hierarchy="secondary"
              onClick={() => doTestPrint()}
              pending={testPrintLoad.pending}
              size="xs"
              text="Print test page"
              theme="normal"
            />
          </>
        ) : null}
      </Flex>
    );
  },
);

const rotations = [0, 90, 180, 270] as const;
type Rotation = (typeof rotations)[number];

const rotateOptions: RedoListItem<Rotation>[] = [
  { id: 0, type: "text", text: "0°", value: 0 },
  { id: 90, type: "text", text: "90°", value: 90 },
  { id: 180, type: "text", text: "180°", value: 180 },
  { id: 270, type: "text", text: "270°", value: 270 },
];

type DuplexOption = "" | "long-edge" | "short-edge" | "one-sided";

const duplexOptions: RedoListItem<DuplexOption>[] = [
  { id: "default", type: "text", text: "Default", value: "" },
  { id: "one-sided", type: "text", text: "One sided", value: "one-sided" },
  { id: "short-edge", type: "text", text: "Short edge", value: "short-edge" },
  { id: "long-edge", type: "text", text: "Long edge", value: "long-edge" },
];
